import React from "react";
import { cn } from "@/lib/utils";

/**
 * Checkbox — controlled/uncontrolled `<input type="checkbox">`
 * primitive wrapped in a label row so click-target = full label.
 *
 * Props:
 *   • label          — visible label content (string or ReactNode)
 *   • description    — optional sub-label under the label
 *   • checked, defaultChecked, onChange — standard React input props
 *   • invalid        — sets aria-invalid + red outline
 *
 * A11y: relies on native semantics; the wrapping `<label>` associates
 * the click target with the input.
 */

export const Checkbox = React.forwardRef(function Checkbox(
  {
    label,
    description,
    className,
    invalid = false,
    disabled = false,
    id,
    "data-testid": testId,
    ...rest
  },
  ref
) {
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const descId = description ? `${inputId}-desc` : undefined;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "inline-flex items-start gap-3 text-[14px]",
        "text-[var(--ds-color-ink)]",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        className
      )}
    >
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={descId}
        data-testid={testId}
        className={cn(
          "mt-[2px] h-4 w-4 shrink-0 rounded-[var(--ds-radius-sm)]",
          "border border-[var(--ds-color-border-strong)]",
          "accent-[var(--ds-color-accent)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-color-background)]",
          invalid && "border-[var(--ds-color-danger)]"
        )}
        {...rest}
      />
      {(label || description) && (
        <span className="flex flex-col leading-snug">
          {label && <span>{label}</span>}
          {description && (
            <span
              id={descId}
              className="text-[13px] text-[var(--ds-color-ink-muted)]"
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
});

Checkbox.displayName = "DSCheckbox";

export default Checkbox;
